import moment from 'moment';
import { HeaderList } from 'src/components/CountTable';
import {
  DailyTimesheet,
  DailyTimesheetPayload,
  EmployeeInfo,
  JobItem,
  WeeklyTimesheet,
} from 'src/redux/timesheet/types';
import { formatDate } from 'src/utils';
import { getDatePayload, getTimeDisplay, getWeekEndDay, getWeekStartDay } from 'src/utils/momentUtils';
import { isEmpty } from 'src/validations';

export const DEFAULT_START_TIME = moment().set({ hour: 6, minute: 45, second: 0, millisecond: 0 }).toDate();
// =================== Table header ================
export const weeklyTableHeader: HeaderList = [
  [
    { title: 'Date', rowSpan: 1, colSpan: 1 },
    { title: 'Start Time', rowSpan: 1, colSpan: 1 },
    { title: 'End Time', rowSpan: 1, colSpan: 1 },
    { title: 'Regular Hours', rowSpan: 1, colSpan: 1 },
    { title: 'OT Hours', rowSpan: 1, colSpan: 1 },
    { title: 'Action', rowSpan: 1, colSpan: 1 },
  ],
];

export const TIMESHEETS_TABLE_TAKE = 'TIMESHEETS_TABLE_TAKE';

export const dailyTableHeader: HeaderList = [
  [
    { title: 'Job *', rowSpan: 1, colSpan: 1 },
    { title: 'Task *', rowSpan: 1, colSpan: 1 },
    { title: 'Lot#s', rowSpan: 1, colSpan: 1 },
    { title: 'Hours *', rowSpan: 1, colSpan: 1 },
    { title: 'Phase Code *', rowSpan: 1, colSpan: 1 },
    { title: 'Extra', rowSpan: 1, colSpan: 1 },
    { title: 'Action', rowSpan: 1, colSpan: 1 },
  ],
];

export const INITIAL_JOB: JobItem = {
  job: null,
  task: '',
  lots: '',
  hours: null,
  phaseCode: null,
  isExtra: false,
};

export const INITIAL_DAILY_TIMESHEET: DailyTimesheet = {
  date: moment().toISOString(),
  isNotWorking: false,
  isNoLunch: false,
  startTime: null,
  endTime: null,
  commentOther: '',
  comments: [],
  jobs: [INITIAL_JOB],
};
export const INITIAL_COMMENTS = [
  { label: 'No work available', value: 'No work available' },
  { label: 'Sick', value: 'Sick' },
  { label: 'Vacation', value: 'Vacation' },
  { label: 'Rain-out', value: 'Rain-out' },
  { label: 'None', value: 'None' },
  { label: 'Other (provide more info)', value: 'Other (provide more info)' },
];

// ==================== Table data helper =================

export const getWeekDays = (startDate?: string | Date, endDate?: string | Date) => {
  const startWeekDate = !!startDate ? startDate : getWeekStartDay();
  const endWeekDate = !!endDate ? endDate : getWeekEndDay();

  let currentWeek: DailyTimesheet[] = [];
  const numberOfDate = moment(endWeekDate).diff(moment(startWeekDate), 'days');

  for (let index = 0; index <= numberOfDate; index++) {
    const date = moment(startWeekDate).add(index, 'day').toISOString();
    const element: DailyTimesheet = {
      date: date,
      isNotWorking: false,
      isNoLunch: false,
      startTime: null,
      endTime: null,
      commentOther: '',
      comments: [],
      jobs: [INITIAL_JOB],
    };
    currentWeek.push(element);
  }
  return currentWeek;
};

const formatDailyTime = (value: Date | null) => {
  if (!!value) return formatDate(value.toISOString(), 'hh:mm A');
  return null;
};

export const getDailyTableDate = (records: DailyTimesheet[], showIndex: number[]) => {
  const data = records.map((item, index) => {
    let totalHours: number = 0;

    item.jobs.forEach(element => {
      const hours = Number(element.hours);
      totalHours += hours;
    });

    return {
      date: moment(item.date).format('ddd, MM/DD'),
      regularHours: totalHours > 8 ? 8 : totalHours,
      otHours: totalHours > 8 ? totalHours - 8 : 0,
      startTime: formatDailyTime(item.startTime),
      endTime: formatDailyTime(item.endTime),
      action: item,
      index: index,
      isShowDetail: showIndex.length > 0 ? showIndex.includes(index) : false,
      isNotWorking: item.isNotWorking,
    };
  });

  return data;
};

export const isDisabledSaveDailyTimesheet = (jobs: JobItem[]) => {
  if (jobs.length === 1) {
    const job = jobs[0];
    return !job.job && !job.task && !job.hours && !job.phaseCode;
  }
  return false;
};

export const isValidHours = (hours: number) => {
  try {
    if (hours === 0 && hours > 24) return false;
    let regex = new RegExp(/^(?:\d*\.(?:[27]5|50?|00?)|\d+\.?)$/, 'g');
    return regex.test(`${hours}`);
  } catch (error) {
    return false;
  }
};

export const getDailyTimesheetPayload = (dailyTimeSheets: DailyTimesheet[]) => {
  const filterData = dailyTimeSheets.filter(data => (!!data.startTime && !data.isNotWorking) || data.isNotWorking);
  const payload: DailyTimesheetPayload[] = filterData.map(data =>
    data.isNotWorking
      ? {
          isNotWorkedToday: data.isNotWorking,
          comment: !isEmpty(data.commentOther) ? data.comments.concat(data.commentOther) : data.comments,
          date: getDatePayload(data.date),
        }
      : ({
          isHavingLunch: !data.isNoLunch,
          startTime: getTimeDisplay(data.startTime.toISOString()),
          isNotWorkedToday: data.isNotWorking,
          comment: !isEmpty(data.commentOther) ? data.comments.concat(data.commentOther) : data.comments,
          date: getDatePayload(data.date),
          detail: data.jobs.map(job => {
            var curentJob = {
              hours: Number(job.hours),
              isExtra: job.isExtra,
              jobCodeId: job.job,
              lot: job.lots,
              phaseCodeId: job.phaseCode,
              task: job.task,
            };

            return curentJob;
          }),
        } as DailyTimesheetPayload),
  );

  return payload;
};

export const convertResponseToDailyTimesheet = (weeklyTableData: WeeklyTimesheet, payload: any[]) => {
  const dailyTimes: DailyTimesheet[] = getWeekDays(weeklyTableData.startDate, weeklyTableData.endDate);

  const updatedData = dailyTimes.map(item => {
    const selectedDate = payload.find(res => res.date === formatDate(item.date, 'YYYY-MM-DD'));

    if (isEmpty(selectedDate)) return item;
    let updatedDate = { ...item };

    updatedDate.id = selectedDate.id;
    updatedDate.endTime = new Date(moment(selectedDate.endTime, 'hh:mm').toISOString());
    updatedDate.startTime = new Date(moment(selectedDate.startTime, 'hh:mm').toISOString());
    updatedDate.isNotWorking = selectedDate['isNotWorking'];
    updatedDate.isNoLunch = !selectedDate.isHavingLunch;
    updatedDate.comments = getComments(selectedDate.comment);
    updatedDate.commentOther = getOtherComment(selectedDate.comment);
    updatedDate.jobs = isEmpty(selectedDate.detail)
      ? [INITIAL_JOB]
      : selectedDate.detail.map(
          (item): JobItem => ({
            job: item.jobCodeId,
            task: item.task,
            lots: item.lot,
            hours: item.hours,
            phaseCode: item.phaseCodeId,
            isExtra: item.isExtra,
          }),
        );
    updatedDate.auditHistoryUri = selectedDate.auditHistoryUri;
    updatedDate.updatedDate = selectedDate.updatedDate;
    updatedDate.createdDate = selectedDate.createdDate;

    var total: number = 0;
    updatedDate.jobs.forEach(job => {
      total += Number(job.hours);
    });
    updatedDate.totalHours = total;
    return updatedDate;
  });
  return updatedData;
};

export const convertResponseToEmployeeInfo = (payload: any) => {
  const { employeeId, employeeNoId, firstName, lastName, level, rate, trade } = payload;
  const employeeInfo: EmployeeInfo = {
    id: employeeId,
    employeeId: employeeNoId,
    level: level,
    trade: trade,
    rate: Number(rate).toFixed(2),
    name: `${lastName}, ${firstName}`,
  };
  return employeeInfo;
};

export const getOtherComment = (comments: string[]) => {
  var comment = '';
  comments.forEach(c => {
    if (!!!INITIAL_COMMENTS.find(e => e.value === c)) {
      comment += c + ' ';
    }
  });
  return comment;
};

export const getComments = (comments: string[]) => {
  var currentComments: string[] = [];
  comments.forEach(c => {
    if (INITIAL_COMMENTS.find(e => e.value === c)) {
      currentComments.push(c);
    }
  });

  return currentComments;
};

export const isInValidJob = (job: JobItem) => {
  var isValidTask = !isEmpty(job.task);

  return !(isValidTask && job.hours > 0 && job.job > 0 && job.phaseCode);
};
